import { useCallback, useEffect, useState } from 'react';
import {
  alertState,
  describeTest,
  disableAlerts,
  enableAlerts,
  readEnvironment,
  sendTestAlert,
} from '../lib/push.js';

/* "Urgent alerts" - the opt-in for P1 push notifications, and the test button.
 *
 * Shown to IT staff on the queue. The API is the real gate (403 for anyone else);
 * showing this only to IT is UX, not security.
 *
 * THE TEST BUTTON IS THE POINT. A broken subscription is completely silent - a
 * revoked permission, an evicted iOS app and a stale endpoint all look exactly
 * like a quiet week - and there is no email behind this. So a person must be able
 * to prove, in one tap, that THIS device will buzz. It reports what the server
 * saw, per device, rather than a bare "ok".
 */

function Icon({ name }) {
  return <span className="material-symbols-outlined" aria-hidden="true">{name}</span>;
}

export default function UrgentAlerts() {
  const [state, setState] = useState(null); // null until the device has been read
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null); // { tone, text }

  const refresh = useCallback(async () => {
    setState(alertState(await readEnvironment()));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function run(job) {
    setBusy(true);
    setNote(null);
    try {
      await job();
    } catch (err) {
      setNote({ tone: 'bad', text: err.message });
    } finally {
      setBusy(false);
      refresh();
    }
  }

  const turnOn = () =>
    run(async () => {
      await enableAlerts();
      setNote({ tone: 'good', text: 'Urgent alerts are on for this device. Press "Send me a test alert" to check it.' });
    });
  const turnOff = () =>
    run(async () => {
      await disableAlerts();
      setNote({ tone: 'good', text: 'Urgent alerts are off for this device.' });
    });
  const test = () =>
    run(async () => {
      setNote(describeTest(await sendTestAlert()));
    });

  if (state === null) return null;

  return (
    <section className="card card--pad alerts" aria-labelledby="alerts-title">
      <h2 id="alerts-title" className="alerts__title">
        <Icon name="notifications_active" /> Urgent alerts
      </h2>

      {state === 'unsupported' && (
        <p className="alerts__text">
          This browser cannot receive urgent alerts. Use Chrome, Edge or Safari on a phone or laptop.
        </p>
      )}

      {state === 'needs-install' && (
        <>
          <p className="alerts__text">
            On an iPhone or iPad, urgent alerts only work from the app on your Home Screen. Do this once:
          </p>
          <ol className="alerts__steps">
            <li>Open this site in Safari.</li>
            <li>Tap Share, then <strong>Add to Home Screen</strong>.</li>
            <li><strong>Open IT Help from the new icon.</strong> Not from Safari.</li>
            <li>Come back to this page and tap <strong>Turn on urgent alerts</strong>.</li>
          </ol>
        </>
      )}

      {state === 'blocked' && (
        <p className="alerts__text">
          Notifications are blocked for this app. Turn them on in your device settings, then come back here.
        </p>
      )}

      {state === 'off' && (
        <>
          <p className="alerts__text">
            Get a notification on this device when a P1 ticket is raised, even when the app is closed.
          </p>
          <button type="button" className="btn btn--primary" onClick={turnOn} disabled={busy}>
            <Icon name="notifications_active" /> Turn on urgent alerts
          </button>
        </>
      )}

      {state === 'on' && (
        <>
          <p className="alerts__text">
            Urgent alerts are <strong>on</strong> for this device. A phone on silent or in a Focus
            mode will not buzz, so allow IT Help through yours.
          </p>
          <div className="alerts__row">
            <button type="button" className="btn btn--primary" onClick={test} disabled={busy}>
              <Icon name="send" /> Send me a test alert
            </button>
            <button type="button" className="btn btn--quiet" onClick={turnOff} disabled={busy}>
              Turn off
            </button>
          </div>
        </>
      )}

      {note && (
        <p className={`alerts__note alerts__note--${note.tone}`} role="status">
          {note.text}
        </p>
      )}
    </section>
  );
}
